import axios from 'axios';
import { useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate unique session ID
const generateSessionId = () => {
  let sessionId = localStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Base tracking function
const trackEvent = async (eventType, data = {}) => {
  try {
    // Track to internal analytics
    const eventData = {
      event_type: eventType,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      device_type: getDeviceType(),
      session_id: generateSessionId(),
      referrer: document.referrer || null,
      ...data
    };

    await axios.post(`${API}/analytics/track`, eventData);
    
    // Also track to Google Analytics
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', eventType, {
        event_category: 'engagement',
        event_label: data.button_name || data.car_model || 'general',
        value: 1,
        custom_parameters: data
      });
    }
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

// Specific tracking functions
export const analytics = {
  // Track page view
  trackPageView: (pageTitle = null) => {
    trackEvent('page_view', {
      page_title: pageTitle || document.title
    });
  },

  // Track button clicks
  trackButtonClick: (buttonName, additionalData = {}) => {
    trackEvent('button_click', {
      button_name: buttonName,
      ...additionalData
    });
  },

  // Track WhatsApp button clicks
  trackWhatsAppClick: (source, carModel = null) => {
    trackEvent('whatsapp_click', {
      button_name: `whatsapp_${source}`,
      car_model: carModel,
      source: source
    });
  },

  // Track car model views
  trackModelView: (carModel, variant = null) => {
    trackEvent('model_view', {
      car_model: carModel,
      variant: variant
    });
  },

  // Track loan calculator usage
  trackCalculatorUse: (calculatorData) => {
    trackEvent('calculator_use', {
      calculator_data: calculatorData
    });
  },

  // Track form submissions
  trackFormSubmit: (formType, formData = {}) => {
    trackEvent('form_submit', {
      form_data: {
        type: formType,
        ...formData
      }
    });
  },

  // Track session start
  trackSessionStart: () => {
    trackEvent('session_start');
  },

  // Track session end
  trackSessionEnd: () => {
    trackEvent('session_end');
  }
};

// Google Analytics Integration
const googleAnalytics = {
  // Track GA4 events
  trackGA4Event: (eventName, parameters = {}) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', eventName, parameters);
    }
  },

  // Track page views
  trackPageView: (pageTitle, pagePath) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', 'G-0ENDPM9GPN', {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: pagePath
      });
    }
  },

  // Track conversions for Google Ads
  trackConversion: (conversionLabel, value = null) => {
    if (typeof window.gtag !== 'undefined') {
      const conversionData = {
        'send_to': `AW-17576285631/${conversionLabel}`
      };
      
      if (value) {
        conversionData.value = value;
        conversionData.currency = 'MYR';
      }
      
      window.gtag('event', 'conversion', conversionData);
    }
  },

  // Track WhatsApp clicks for ads
  trackWhatsAppConversion: (source, carModel = null) => {
    googleAnalytics.trackGA4Event('whatsapp_click', {
      event_category: 'engagement',
      event_label: source,
      car_model: carModel,
      value: 1
    });
    
    // Track as conversion for Google Ads (you'll need to get conversion label from Google Ads)
    googleAnalytics.trackConversion('WHATSAPP_CONVERSION_LABEL');
  },

  // Track form submissions for ads  
  trackFormConversion: (formType) => {
    googleAnalytics.trackGA4Event('form_submit', {
      event_category: 'engagement',
      event_label: formType,
      value: 1
    });
    
    // Track as conversion for Google Ads (you'll need to get conversion label from Google Ads)
    googleAnalytics.trackConversion('FORM_CONVERSION_LABEL');
  }
};

// Auto-track page views when component mounts
export const usePageTracking = (pageTitle) => {
  useEffect(() => {
    analytics.trackPageView(pageTitle);
  }, [pageTitle]);
};

// Session management
export const initializeAnalytics = () => {
  // Track session start
  analytics.trackSessionStart();

  // Track page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      analytics.trackSessionEnd();
    }
  });

  // Track before page unload
  window.addEventListener('beforeunload', () => {
    analytics.trackSessionEnd();
  });

  // Auto-track page views on route changes
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function(...args) {
    originalPushState.apply(history, args);
    setTimeout(() => analytics.trackPageView(), 0);
  };

  history.replaceState = function(...args) {
    originalReplaceState.apply(history, args);
    setTimeout(() => analytics.trackPageView(), 0);
  };

  window.addEventListener('popstate', () => {
    setTimeout(() => analytics.trackPageView(), 0);
  });
};

export default analytics;
export { googleAnalytics };